import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Flex, Image, Text, Tooltip, VStack } from '@bonusly/design-system';

import bonuslyMark from '../../../../../assets/images/bonusly-logo.mark.svg';
import { Bonus } from '../../../../../types/bonus';
import { GithubLogo } from '../../../../github/github-logo';
import { GranolaLogo } from '../../../../granola/granola-logo';
import { LinearLogo } from '../../../../linear/linear-logo';
import { MsTeamsLogo } from '../../../../ms-teams/ms-teams-logo';
import { SlackLogo } from '../../../../slack/slack-logo';

/**
 * Known `bizy_sources` values: the external connectors plus `bonusly` for
 * Bizy's own data. Unknown values from newer backends render nothing.
 */
const SOURCE_CHIPS: Record<string, { icon: ReactNode; bg: string }> = {
	slack: { icon: <SlackLogo width="12px" height="12px" />, bg: 'gray.25' },
	github: { icon: <GithubLogo width="12px" height="12px" />, bg: 'gray.25' },
	linear: { icon: <LinearLogo width="12px" height="12px" />, bg: 'purple.25' },
	granola: { icon: <GranolaLogo width="12px" height="12px" />, bg: 'gray.25' },
	ms_teams: { icon: <MsTeamsLogo width="12px" height="12px" />, bg: 'purple.25' },
	bonusly: { icon: <Image src={bonuslyMark} alt="" boxSize="12px" />, bg: 'green.25' },
};

const SourceChip: FC<{ source: string }> = ({ source }) => {
	const chip = SOURCE_CHIPS[source];
	if (!chip) return null;

	return (
		<Flex
			align="center"
			justify="center"
			boxSize="24px"
			borderRadius="full"
			bg={chip.bg}
			// The chip bg is always light, so pin the icon color: currentColor
			// logos (GitHub, Linear, Granola) would otherwise inherit white
			// inside the dark tooltip and disappear.
			color="gray.900"
			border="1px solid"
			borderColor="bg.surface"
			boxShadow="sm"
			flexShrink={0}
		>
			{chip.icon}
		</Flex>
	);
};

interface BizyAttributionProps {
	bonus: Bonus;
	/**
	 * Sizes the label/dot for the compact comment row (fixed `sm`, dot always
	 * inline) instead of the responsive card-header treatment (`md` on sm+, dot
	 * hidden while it stacks above the timestamp on mobile).
	 */
	compact?: boolean;
}

/**
 * "Sent with Bizy's help" attribution for bonus card headers and comments. In card
 * headers it renders via `BonusHeaderMeta`, which stacks it above the timestamp
 * on mobile and places it inline on sm+. Hovering the label shows source types
 * when `bizy_sources` is present. Types only — never excerpts.
 */
export const BizyAttribution: FC<BizyAttributionProps> = ({ bonus, compact = false }) => {
	const { t } = useTranslation('recognition');

	if (bonus.via !== 'bizy') return null;

	const sources = (bonus.bizy_sources ?? []).filter((source) => source in SOURCE_CHIPS);
	const label = t('bonus_card.bizy_attribution.sent_with_bizy');
	const labelFontSize = compact ? 'sm' : { base: 'sm', sm: 'md' };
	const dotDisplay = compact ? 'inline' : { base: 'none', sm: 'inline' };

	const sourcesTooltip = (
		<VStack align="stretch" spacing={1} py={1}>
			<Text fontSize="xs" fontWeight="medium">
				{t('bonus_card.bizy_attribution.sources_tooltip_title')}
			</Text>
			{sources.map((source) => (
				<Flex key={source} align="center" gap={1.5}>
					<SourceChip source={source} />
					<Text fontSize="xs">{t(`bonus_card.bizy_attribution.source_names.${source}`)}</Text>
				</Flex>
			))}
		</VStack>
	);

	return (
		<Flex align="center" data-testid="bizy-attribution">
			{sources.length > 0 ? (
				<Tooltip label={sourcesTooltip} placement="bottom-start">
					{/* A real button so the tooltip opens on keyboard focus, the label is
					    exposed to assistive tech, and the bonus card's closest('button')
					    click-guard keeps clicks from navigating. */}
					<Text
						as="button"
						type="button"
						fontSize={labelFontSize}
						color="text.softest"
						fontWeight="normal"
						cursor="default"
						aria-label={`${label}, ${t('bonus_card.bizy_attribution.chips_label')}`}
					>
						{label}
					</Text>
				</Tooltip>
			) : (
				<Text fontSize={labelFontSize} color="text.softest" fontWeight="normal">
					{label}
				</Text>
			)}
			<Text as="span" color="text.softest" px={1} aria-hidden="true" display={dotDisplay}>
				·
			</Text>
		</Flex>
	);
};
